import {
	Component,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { VideoQualityUtil } from '@go-modules/utilities/video-quality/video-quality.util';
import { umcChooserToken } from 'go-modules/universal-media-chooser/umc.factory';
import { Inject } from '@angular/core';
import { GoRecorderManagerServiceInterface, goRecorderManagerServiceToken } from 'go-modules/feedback-session/services/go-recorder-manager.service';
import { FeedbackSessionInterface, feedbackSessionProviderToken } from 'go-modules/feedback-session/feedback-session.provider';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { VideoSceneController } from 'go-modules/video-scene/video-scene.controller';
import { States } from 'go-modules/video-scene/state-emitter/state-emitter';
import { RecordingStoppedState } from 'go-modules/video-scene/recording-stopped-state';
import { isObject } from 'lodash';
import { PubnubService, pubnubToken } from 'go-modules/go-pubnub/pubnub.service';
import { MediaSchema } from 'go-modules/models/media';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { CommonModule } from '@angular/common';
import { UniversalMediaChooserDirective } from '@go-modules/angularjs-wrappers/universal-media-chooser.directive';
import type { User as UserFactory } from 'go-modules/models/user/user.factory';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { Group as GroupModel } from 'go-modules/models/group-dep/group.factory';

@Component({
	selector: 'ngx-feedback-session-record-view',
	templateUrl: './feedback-session-record-view.component.html',
	styleUrls: ['./feedback-session-record-view.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		UniversalMediaChooserDirective
	]
})

export class FeedbackSessionRecordViewComponent implements OnInit, OnDestroy {
	@Input() public exit?: () => void;
	@Input() public requestExit?: () => void;
	@Input() public postSession?: () => Promise<void>;
	@Input() public setRecordingStoppedState?: (state: RecordingStoppedState) => void;
	@Input() public wasRecordingStoppedByMe?: () => boolean;
	@Input() public hasSingleMediaDisplay = false;

	public umcOptions;
	public isWhiteLabel = false;
	public startedByMe = false;
	public recorder!: VideoSceneController;
	private eventNames: EVENT_NAMES[] = [];
	private eventSubscription?: Subscription;
	private activity: ActivityModel;
	private session?: ReturnType<typeof SessionModel>;
	private userGroup: ReturnType<typeof GroupModel> | null = null;
	private user: ReturnType<typeof UserFactory>;

	constructor (
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(umcChooserToken) private umcChooser: { MEDIA_TYPE },
		@Inject(sessionToken) private Session: ReturnType<typeof SessionModel>,
		@Inject(goRecorderManagerServiceToken) private goRecorderManagerService: GoRecorderManagerServiceInterface,
		@Inject(feedbackSessionProviderToken) private feedbackSession: FeedbackSessionInterface,
		@Inject(pubnubToken) private pubnub: PubnubService,
		@Inject(userServiceToken) private userService: UserService,
		private eventService: EventService,
		private feedbackSessionService: FeedbackSessionService,
		private environmentVarsService: EnvironmentVarsService
	) {}

	public ngOnInit (): void {
		this.isWhiteLabel = this.environmentVarsService.get(EnvironmentVarsService.VAR.WHITELABEL);
		this.startedByMe = false;

		this.session = this.selectedService.getSession();
		this.activity = this.selectedService.getActivity();
		this.userGroup = this.selectedService.getGroup();
		this.user = this.userService.currentUser;

		if (!this.session || !this.activity) {
			return;
		}

		this.umcOptions = {
			defaultSection: this.umcChooser.MEDIA_TYPE.RECORD_VIDEO,
			groupId: this.session.group_id,
			media: this.session.media,
			resourceId: this.session.session_id,
			resourceType: this.Session.RESOURCE_TYPE,
			goRecorder: {
				overlayNameEnabled: this.activity.is_conversation,
				showCaptionButton: this.activity.is_conversation,
				hideTimeDisplay: this.activity.is_conversation,
				isMultiCam: this.activity.is_conversation,
				timeLimit: this.activity.time_limit,
				disableResume: this.activity.has_single_recording_attempt,
				displayName: this.activity.isConversation?.()
					? this.user?.getFullName?.() ?? ''
					: '',
				videoQuality: this.selectedService.getLicense()?.salesforce_license.video_quality ??
					VideoQualityUtil.MINIMUM_RESOLUTION
			}
		};

		if (!this.hasSingleMediaDisplay || this.activity.has_single_recording_attempt) {
			this.umcOptions.goRecorder.stateLabels = {
				uninitialized: 'media-recorder_controller-record',
				initialized: 'media-recorder_controller-record',
				active: 'media-recorder_controller-record',
				not_ready: 'feedback-session-media-display_begin',
				ready: 'feedback-session-media-display_begin',
				starting: 'media-recorder_controller-pause',
				recording: 'media-recorder_controller-pause',
				pausing: 'feedback-session-media-display_resume',
				paused: 'feedback-session-media-display_resume',
				stopping: 'common_stop',
				stopped: 'common_stop'
			};
			this.umcOptions.goRecorder.stateAnalyticsActions = {
				not_ready: 'begin',
				ready: 'begin',
				starting: 'pause',
				recording: 'pause',
				pausing: 'resume',
				paused: 'resume',
				stopping: 'record',
				stopped: 'record'
			};
		}

		// Show/hide feedback panel
		const showPanel = this.activity.hasRecordingInstructions?.() ?? false;
		this.feedbackSessionService.togglePanel(showPanel);

		// Notifications... group is no longer active
		if (this.userGroup?.past) {
			this.eventService.broadcast(EVENT_NAMES.FS_NOTIFICATION_ADD, 'GROUP_INACTIVE');
		}

		this.eventNames = [
			EVENT_NAMES.FEEDBACK_SESSION_DELETED,
			EVENT_NAMES.FEEDBACK_SESSION_POSTED,
			EVENT_NAMES.FEEDBACK_SESSION_DISCARD,
			EVENT_NAMES.FEEDBACK_SESSION_STATUS_CHANGE
		];

		this.eventSubscription = this.eventService.events
			.pipe(filter((ev: GoEvent) => this.eventNames.includes(ev.name as EVENT_NAMES)))
			.subscribe((ev: GoEvent) => {
				switch (ev.name) {
					case EVENT_NAMES.FEEDBACK_SESSION_DELETED:
						this.stopRecording().then(() => {
							this.exit?.();
						});
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_POSTED:
					case EVENT_NAMES.FEEDBACK_SESSION_DISCARD:
						this.stopRecording();
						break;
					case EVENT_NAMES.FEEDBACK_SESSION_STATUS_CHANGE:
						const status = ev.data.status;
						const user = ev.data.user;
						switch (status) {
							case this.Session.LIVE:
								// Session started by another user or not a Multi-camera Meeting Session
								if (!this.startedByMe && !this.activity.is_conversation) {
									// Since the recording has already been started, swap the display mode to live
									this.feedbackSession.setMediaDisplayMode(
										this.feedbackSession.MediaDisplayMode.LIVE);

									// Ensure feedback tools are enabled
									this.feedbackSessionService.togglePanel(true);

									// Show notification message
									this.eventService.broadcast(EVENT_NAMES.FS_NOTIFICATION_ADD, 'STARTED_BY_REMOTE_DEVICE');
								}
								break;
							case this.Session.RECORDED:
								// In response to remote stop request
								if (isObject(user)) {
									this.stopRecording().then(() => {
										this.eventService.broadcast(EVENT_NAMES.FS_NOTIFICATION_ADD, 'STOPPED_BY_REMOTE_DEVICE');
									});
								}
								break;
						}
				}
			});
	}

	public ngOnDestroy (): void {
		if (this.session) {
			this.goRecorderManagerService.unregister(
				this.feedbackSession.sessionRecorderId(this.session)
			);
		}
		this.eventSubscription?.unsubscribe();
	}

	public isRecordEnabled (): boolean {
		return !this.userGroup?.past &&
			!this.feedbackSession.options.disableSessionRecording;
	}

	public onMediaDetermined (media: MediaSchema): void {
		if (!this.session?.media) {
			this.session?.setMedia?.(media);
			this.session?.save?.();
		}
	}

	public onRecorderInit (recorder: VideoSceneController): void {
		if (this.session) {
			const recorderId = this.feedbackSession.sessionRecorderId(this.session);
			this.goRecorderManagerService.register(recorderId, recorder);
		}

		recorder.on('STATE_CHANGE', (state: States) => {
			switch (state) {
				case States.STARTING:
					this.handleRecorderStarting();
					break;
				case States.PAUSING:
					this.handleRecorderPause();
					break;
				case States.STOPPED:
					this.handleRecorderStopped();
					break;
			}
		});

		this.recorder = recorder;
	}

	public stopRecording (): Promise<void> {
		if (!this.recorder) {
			return Promise.resolve();
		}

		const isRecording: boolean = [States.STARTING, States.RECORDING].includes(this.recorder.state);

		if (!isRecording) {
			return Promise.resolve();
		}

		return this.recorder.stop();
	}

	private handleRecorderStarting (): void {
		this.startedByMe = true;
		this.session?.setStatus?.(this.Session.LIVE);
	}

	private handleRecorderPause (): void {
		this.session?.setStatus?.(this.Session.RECORDED);
	}

	private handleRecorderStopped (): void {
		this.setRecordingStoppedState?.(this.recorder.getRecordingStoppedState());

		const pubnubChannel = this.session?.settings?.pubnub?.pubnub_channel;
		if (pubnubChannel) {
			this.pubnub.pub(
				pubnubChannel,
				{ recordingManuallyEnded: true }
			);
		}

		// If instant playback is not available, auto post and exit
		// immediately (don't make the user wait for processing to finish).
		const stoppedByMe = this.wasRecordingStoppedByMe?.() ?? false;

		if (!this.recorder.isInstantPlaybackAvailable()) {
			if (stoppedByMe) {
				this.exitAfterPostIfNeeded();
			} else {
				this.exit?.();
			}
			return;
		}

		if (this.feedbackSession.options.manualPostDiscard && this.hasSingleMediaDisplay) {
			this.feedbackSession.setMediaDisplayMode(this.feedbackSession.MediaDisplayMode.PLAYBACK);
		} else if (
			this.activity?.has_single_recording_attempt ||
			this.timeLimitReached() ||
			(this.session?.source_media && this.activity?.has_response_media)
		) {
			this.exitAfterPostIfNeeded();
		} else {
			this.requestExit?.();
		}
	}

	private exitAfterPostIfNeeded (): void {
		if (this.activity?.has_single_recording_attempt) {
			if (this.postSession) {
				this.postSession().then(() => {
					this.exit?.();
				});
			} else {
				this.exit?.();
			}
		} else {
			this.session?.setStatus?.(this.Session.RECORDED);
			this.requestExit?.();
		}
	}

	private timeLimitReached (): boolean {
		const limit = this.activity?.time_limit ?? 0;
		return limit > 0 && (this.recorder.getDuration() / 1000) >= limit * 60;
	}

}
